export enum PaymentStatus {
  UNPAID = 'unpaid',
  AUTHORIZED = 'authorized',
  PAID = 'paid',
  PARTIAL_CAPTURE = 'partial_capture',
  PARTIAL_REFUND = 'partial_refund',
  REFUNDED = 'refunded',
  RETURNED = 'returned',
}

export enum PaymentMethod {
  PAYPAL = 'paypal',
  STRIPE = 'stripe',
  NPA = 'npa',
  CREDIT_CARD = 'credit_card',
  BANK_TRANSFER = 'bank_transfer',
  SEND_REQUEST = 'send_request',
  ZELLE = 'zelle',
  CHECK = 'check',
  ACH = 'ach',
  WIRE = 'wire',
  CASH = 'cash',
  OTHER = 'other',
}

export interface Address {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  company: string;
}

export interface MetadataNote {
  id: string;
  text: string;
  author: string;
  createdAt: string;
}

export interface MetadataFile {
  id: string;
  url: string;
  key: string;
  name: string;
  category?: string;
  uploadedAt: string;
}

export interface MetadataReview {
  id: string;
  createdAt: string;
  [key: string]: unknown;
}

export interface MetadataRequest {
  status: string;
  reason: string;
  requestedAt: string;
  requestedBy: string;
}

export interface PaymentMetadata {
  // Set at order creation
  sessionId?: string;
  promotionCode?: string | null;
  paymentMethod?: string;
  latestPaymentMethod?: string;
  paymentMethodUpdatedAt?: string;
  /** Set when staff confirm a payment received outside checkout; latestPaymentMethod keeps how it was ordered */
  paidVia?: string;
  paidViaConfirmedAt?: string;

  // Updated via API
  eta?: string;
  flag?: string;
  notes?: MetadataNote[];
  files?: MetadataFile[];
  salespersonFiles?: MetadataFile[];
  /** Set via /warehouse/orders/:id/notes and /issues — author is a userId, not a name */
  warehouseNotes?: { id: string; text: string; author: string; createdAt: string }[];
  /** Set via POST /warehouse/orders/:id/files */
  warehouseFiles?: { id: string; url: string; key: string; name: string; uploadedBy: string; uploadedAt: string }[];
  /** Set via POST/DELETE /warehouse/orders/:id/shipping-documents/:type — fixed 3-slot map, not a list. */
  shippingDocuments?: Partial<
    Record<
      'invoice' | 'label' | 'bill_of_lading',
      { url: string; key: string; name: string; uploadedBy: string; uploadedAt: string }
    >
  >;
  paymentActions?: unknown[];
  reviews?: MetadataReview[];
  cancellation?: { reason: string; cancelledAt: string };
  cancellationRequest?: MetadataRequest;
  returnRequest?: MetadataRequest;
  /** Sales tax exemption certificate uploaded by the customer (file is in `files`) */
  taxExemptionRequest?: {
    status: 'pending' | 'approved' | 'rejected';
    fileId: string;
    submittedAt: string;
    reviewedAt?: string;
    reviewedById?: string | null;
  };

  // Child order fields
  isChildOrder?: boolean;
  parentOrderNumber?: string;
  reason?: string;
  message?: string;

  [key: string]: unknown;
}

export interface PaymentTransaction {
  id: string;
  orderId: string;
  type: string;
  paymentMethod: string;
  amount: string;
  status: PaymentStatus;
  gateway: string | null;
  gatewayTransactionId: string;
  metadata: Record<string, unknown>;
  processedById: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}
